import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Truck, LogOut } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = () => api.get("/staff/pickups").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]">
      <header className="bg-secondary text-secondary-foreground px-4 sm:px-6 h-16 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <BrandMark dark iconSize={32} />
          <div className="text-[10px] uppercase tracking-widest text-secondary-foreground/60 border-l border-white/20 pl-3">Staff Pickups</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm hidden sm:block">{user?.name}</span>
          <Button size="sm" variant="ghost" className="text-secondary-foreground" onClick={() => { logout(); navigate("/"); }} data-testid="staff-logout"><LogOut size={15} /> Logout</Button>
        </div>
      </header>

      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <h1 className="font-heading font-extrabold text-2xl flex items-center gap-2"><Truck className="text-primary" /> My Pickups</h1>
        <div className="mt-6 space-y-3">
          {rows.length === 0 && <div className="text-muted-foreground text-center py-10 bg-card border border-border rounded-lg">No assigned pickups.</div>}
          {rows.map((t) => <PickupCard key={t.trade_id} t={t} reload={load} />)}
        </div>
      </div>
    </div>
  );
}

function PickupCard({ t, reload }) {
  const [weight, setWeight] = useState(t.actual_weight || "");
  const record = async () => {
    try { await api.post(`/staff/pickups/${t.trade_id}/collect`, { actual_weight: parseFloat(weight) }); toast.success("Weight recorded"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={`staff-pickup-${t.trade_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading font-bold font-mono-points">#{t.trade_id}</div>
          <div className="text-sm">{t.customer_name} · {t.phone}</div>
          <div className="text-xs text-muted-foreground">{t.address}</div>
          <div className="text-xs text-muted-foreground">Pickup {t.pickup_date} ({t.pickup_time}) · est ~{t.estimated_kg}kg</div>
        </div>
        <span className="text-xs font-semibold px-2 py-1 rounded bg-primary/15 text-primary">{t.status}</span>
      </div>
      {t.status !== "Weighed" ? (
        <div className="mt-3 flex gap-2 items-end">
          <div><Label className="text-xs">Collected weight (kg)</Label><Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="h-9 w-32" data-testid={`staff-weight-${t.trade_id}`} /></div>
          <Button onClick={record} data-testid={`staff-record-${t.trade_id}`}>Record collection</Button>
        </div>
      ) : (
        <div className="mt-3 text-sm text-[hsl(var(--secondary))] font-semibold">✓ Weighed {t.actual_weight} kg — awaiting admin acceptance</div>
      )}
    </div>
  );
}
